import { z } from "zod";
import type { Address } from "../site/address.js";
import { address as makeAddress } from "../site/address.js";
import type { Building } from "../site/building.js";
import { building as makeBuilding } from "../site/building.js";
import type { Gps, GpsSource } from "../site/gps.js";
import type { SitePhotoRef } from "../site/photo.js";
import type { Site } from "../site/site.js";
import { site as makeSite } from "../site/site.js";
import { CodecError, instantSchema } from "./envelope.js";

/**
 * Phase 6a codec for `Site` and its sub-types: `Address`, `Gps`,
 * `SitePhotoRef`, `Building`. Determinism: declared key order
 * (id, name, address, gps, photos, buildings, createdAt, ownerId);
 * undefined optionals are omitted; arrays preserve domain order.
 *
 * `createdAt` and `gps.capturedAt` are validated as canonical instants.
 * The smart factory `site` re-runs the postal-code validation, so a
 * decoded envelope with a malformed address fails fast.
 */

export const addressJsonSchema = z.object({
  street: z.string(),
  unit: z.string().optional(),
  city: z.string(),
  region: z.string(),
  postalCode: z.string(),
  country: z.string().min(1),
});

export type AddressJson = z.infer<typeof addressJsonSchema>;

export function encodeAddress(a: Address): Record<string, unknown> {
  const out: Record<string, unknown> = { street: a.street };
  if (a.unit !== undefined) out["unit"] = a.unit;
  out["city"] = a.city;
  out["region"] = a.region;
  out["postalCode"] = a.postalCode;
  out["country"] = a.country;
  return out;
}

export function decodeAddress(input: unknown): Address {
  const parsed = addressJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeAddress: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return makeAddress({
    street: j.street,
    ...(j.unit !== undefined ? { unit: j.unit } : {}),
    city: j.city,
    region: j.region,
    postalCode: j.postalCode,
    country: j.country,
  });
}

const gpsSourceSchema: z.ZodType<GpsSource> = z.enum([
  "manual",
  "auto-photo",
  "auto-device",
]);

export const gpsJsonSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  capturedAt: instantSchema,
  source: gpsSourceSchema,
});

export type GpsJson = z.infer<typeof gpsJsonSchema>;

export function encodeGps(g: Gps): GpsJson {
  return {
    lat: g.lat,
    lng: g.lng,
    capturedAt: g.capturedAt,
    source: g.source,
  };
}

export function decodeGps(input: unknown): Gps {
  const parsed = gpsJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeGps: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

export const sitePhotoRefJsonSchema = z.object({
  id: z.string().min(1),
});

export type SitePhotoRefJson = z.infer<typeof sitePhotoRefJsonSchema>;

export function encodeSitePhotoRef(p: SitePhotoRef): SitePhotoRefJson {
  return { id: p.id };
}

export function decodeSitePhotoRef(j: SitePhotoRefJson): SitePhotoRef {
  return { id: j.id };
}

export const buildingJsonSchema = z.object({
  id: z.string().min(1),
  name: z.string().optional(),
  panelIds: z.array(z.string().min(1)),
});

export type BuildingJson = z.infer<typeof buildingJsonSchema>;

export function encodeBuilding(b: Building): Record<string, unknown> {
  const out: Record<string, unknown> = { id: b.id };
  if (b.name !== undefined) out["name"] = b.name;
  out["panelIds"] = [...b.panelIds];
  return out;
}

export function decodeBuilding(input: unknown): Building {
  const parsed = buildingJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeBuilding: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return makeBuilding({
    id: j.id,
    ...(j.name !== undefined ? { name: j.name } : {}),
    panelIds: j.panelIds,
  });
}

export const siteJsonSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  address: addressJsonSchema,
  gps: gpsJsonSchema.optional(),
  photos: z.array(sitePhotoRefJsonSchema),
  buildings: z.array(buildingJsonSchema),
  createdAt: instantSchema,
  ownerId: z.string().min(1),
});

export type SiteJson = z.infer<typeof siteJsonSchema>;

export function encodeSite(s: Site): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: s.id,
    name: s.name,
    address: encodeAddress(s.address),
  };
  if (s.gps !== undefined) out["gps"] = encodeGps(s.gps);
  out["photos"] = s.photos.map(encodeSitePhotoRef);
  out["buildings"] = s.buildings.map(encodeBuilding);
  out["createdAt"] = s.createdAt;
  out["ownerId"] = s.ownerId;
  return out;
}

export function decodeSite(input: unknown): Site {
  const parsed = siteJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeSite: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return makeSite({
    id: j.id,
    name: j.name,
    address: decodeAddress(j.address),
    ...(j.gps !== undefined ? { gps: decodeGps(j.gps) } : {}),
    photos: j.photos.map(decodeSitePhotoRef),
    buildings: j.buildings.map(decodeBuilding),
    createdAt: j.createdAt,
    ownerId: j.ownerId,
  });
}
